import React from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * One pager, for every long ISP table.
 *
 * Ray, 2026-09-03: *"make sure accounts, customers, billing has pagination."*
 * An operator with 300 subscribers currently gets 300 rows and a browser that
 * stops responding on a phone.
 *
 * 🔴 CLIENT SIDE, ON PURPOSE. The filters and search already work across the
 * whole list; paging on the server would mean searching only the page you can
 * see, which is worse than a long page. When a fleet is big enough that the
 * fetch itself hurts, the fix is a server search, not a server page.
 */

export const PAGE_SIZE = 25;

interface Props {
   page: number;
   total: number;
   onPage: (n: number) => void;
   /** What the rows are, for the count line: "customers", "accounts". */
   noun: string;
}

const IspPager: React.FC<Props> = ({ page, total, onPage, noun }) => {
   const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
   // One page is not a pager. Showing "1 of 1" is chrome that says nothing.
   if (pages <= 1) return null;

   const from = page * PAGE_SIZE + 1;
   const to = Math.min(total, (page + 1) * PAGE_SIZE);

   return (
      <Row>
         <Count>
            {from}–{to} of {total} {noun}
         </Count>
         <Btns>
            <Btn
               type="button"
               disabled={page === 0}
               onClick={() => onPage(page - 1)}
            >
               Previous
            </Btn>
            <Where>
               {page + 1} / {pages}
            </Where>
            <Btn
               type="button"
               disabled={page >= pages - 1}
               onClick={() => onPage(page + 1)}
            >
               Next
            </Btn>
         </Btns>
      </Row>
   );
};

const Row = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   margin-top: 12px;
   flex-wrap: wrap;
`;
const Count = styled.div`
   font-size: 12.5px;
   color: ${colors.GRAY5};
`;
const Btns = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;
const Where = styled.span`
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.GRAY5};
   min-width: 46px;
   text-align: center;
`;
const Btn = styled.button`
   font-family: inherit;
   font-size: 12.5px;
   font-weight: 700;
   padding: 7px 14px;
   border-radius: 9px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.WHITE};
   color: ${colors.PRIMARY};
   cursor: pointer;

   &:disabled {
      opacity: 0.45;
      cursor: default;
      color: ${colors.GRAY5};
   }
`;

export default IspPager;
